import { styled, useTheme } from "styled-components";
import { DateTime } from "luxon";
import { useHistoryReplayContext } from "./history-replay-context";
import { HistoryTimeLinePlayhead } from "./history-timeline-playhead";
import { HistoryTimeLineClickarea } from "./history-timeline-clickarea";
import { convertPositionToTimeText } from "./history-replay.utils";
import { HistoryTimelineEvents } from "./history-timeline-events";

const height = 60;

type Props = {
  width: number;
};
export function HistoryTimelineContainer({ width }: Props) {
  const {
    timeLineContainerSize,
    timeLineMeasureRef,
    timelineDomainInSeconds,
    isLoading,
  } = useHistoryReplayContext();

  const theme = useTheme();

  if (width <= 0) {
    return null;
  }

  // How many small vertical lines to draw in the timeline
  const amountOfLines = 24;
  // The spacing between lines
  const lineSpacing = width / amountOfLines;
  // The amount of time indicators shown in the timeline (08:00, 09:00 etc.)
  const timeIndicators = amountOfLines / 3;
  // Time indicator spacing
  const timeIndicatorSpacing = width / timeIndicators;
  // Spacing between the longer lines (that include a time indicatior)
  const longLineSpacing = 3;

  return (
    <Container ref={timeLineMeasureRef}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        style={{ pointerEvents: "none" }}
      >
        <g stroke={isLoading ? theme.colors.lightNeutral : "black"}>
          <line x1={0} x2={width} y1={1} y2={1} />
          {Array.from({ length: amountOfLines + 1 }, (_, i) => {
            // Move the first line one pixel to the right
            // Move the last line one pixel to the left
            // This is done to ensure that the first and last line are properly visible
            const x =
              i * lineSpacing + (i === 0 ? 1 : i === amountOfLines ? -1 : 0);
            return (
              <line
                key={i}
                x1={x}
                x2={x}
                y1="0"
                y2={i % longLineSpacing === 0 ? "30" : "15"}
              />
            );
          })}
        </g>
      </svg>
      <TimeIndicatorContainer>
        {Array.from({ length: timeIndicators }, (_, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: timeIndicatorSpacing * i + 12,
              bottom: timeLineContainerSize.height - 15,
            }}
          >
            <TimelineText $isLoading={isLoading}>
              {convertPositionToTimeText(
                timeIndicatorSpacing * i,
                [0, timeLineContainerSize.width],
                timelineDomainInSeconds,
                DateTime.TIME_SIMPLE,
              )}
            </TimelineText>
          </div>
        ))}
      </TimeIndicatorContainer>
      <HistoryTimeLinePlayhead />
      <HistoryTimelineEvents />
      <HistoryTimeLineClickarea />
    </Container>
  );
}

const TimelineText = styled.span<{ $isLoading: boolean }>`
  white-space: nowrap;
  ${(p) => p.theme.typographies.bodySmallLight};
  color: ${(p) =>
    p.$isLoading ? p.theme.colors.lightNeutral : p.theme.colors.black};
  @media (max-width: ${(p) => p.theme.breakpoints.desktop.max}px) {
    ${(p) => p.theme.typographies.bodyExtraSmallLight};
  }
`;
const TimeIndicatorContainer = styled.div`
  position: relative;
  pointer-events: none;
`;
const Container = styled.div`
  width: 100%;
  padding-top: ${(p) => p.theme.space("xsmall")};
  height: 50%;
`;
